import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import MainPageLayout from "../../../components/header/MainPageLayout";
import BackButton from "../../../components/button/BackButton/BackButton";
import {
    TableCard,
    PageTitleSection,
    PageTitle,
    ActionsCard,
    ActionButtons,
} from "./InstitutionsTable.styles";
import { GetAllInstitutionsType } from "../../../core/ServerApiLayer/VOne/institutions/get_all_instititions";
import {
    getJwt,
    getUserRoleAndEntity,
} from "../../../core/LocalStorage/AuthSession/core";

interface InstitutionOverviewProps {
    getAllInstitutions: GetAllInstitutionsType;
}

/**
 * Landing page for a single institution (/institution/:institutionId).
 *
 * The hinge of the nested navigation: from here you drill into either the
 * institution's departments or all of its users, and "Back" returns to the
 * institutions list. Institutions do not always have departments, so both
 * choices live here side by side rather than forcing you through departments.
 */
export const InstitutionOverview: React.FC<InstitutionOverviewProps> = ({
    getAllInstitutions,
}) => {
    const navigate = useNavigate();
    const { institutionId } = useParams<{ institutionId: string }>();
    const [name, setName] = useState<string>("");

    useEffect(() => {
        async function fetchName() {
            const jwt = getJwt();
            if (!jwt || !institutionId) return;
            const response = await getAllInstitutions(jwt);
            if (response.status === 200 && response.data) {
                const match = response.data.find(
                    (institution) =>
                        String(institution.entity_id) === institutionId,
                );
                if (match) setName(match.name);
            }
        }
        fetchName();
    }, [getAllInstitutions, institutionId]);

    // A superadmin drilled in from the all-institutions list; an institution
    // admin arrived from their landing page and cannot view that list.
    const isSuperadmin = getUserRoleAndEntity().role === "SUPERADMIN";
    const backTo = isSuperadmin ? "/institutions" : "/home";

    // Both choices are peers, so give them an identical footprint wide enough to
    // hold the longer label on one line (the shared button is only 150px, which
    // forces "View Departments" to wrap and look taller than "View All Users").
    const choiceButtonStyle = {
        width: "13rem",
        whiteSpace: "nowrap" as const,
    };

    return (
        <MainPageLayout>
            <BackButton navigate={navigate} to={backTo} />
            <TableCard>
                <PageTitleSection>
                    <PageTitle>{name || "Institution"}</PageTitle>
                </PageTitleSection>
                <ActionsCard>
                    <ActionButtons
                        backgroundColorHex="#278230"
                        style={choiceButtonStyle}
                        onClick={() =>
                            navigate(
                                `/institution/${institutionId}/departments`,
                            )
                        }
                    >
                        View Departments
                    </ActionButtons>
                    <ActionButtons
                        backgroundColorHex="#cc0000"
                        style={choiceButtonStyle}
                        onClick={() =>
                            navigate(`/institution/${institutionId}/users`)
                        }
                    >
                        View All Users
                    </ActionButtons>
                </ActionsCard>
            </TableCard>
        </MainPageLayout>
    );
};

export default InstitutionOverview;
